import React, {ReactElement} from "react";
import {graphql, PageProps} from "gatsby";

import FlexibleBaseContentModel, {
  FlexibleBaseContentModelProps,
} from "/src/components/flexible-base-content-model/FlexibleBaseContentModel";
import WidgetShare from "/src/components/widgets/widget-share/WidgetShare";
import Layout from "/src/components/layouts/layout/Layout";
import BlogHero from "/src/components/hero-side-by-side-module/blog-hero/BlogHero";
import HeaderInfo from "/src/components/header-info/HeaderInfo";
import GetStartedModule, {
  GetStartedModuleProps,
} from "/src/components/get-started-module/GetStartedModule";
import BreadcrumbsShareCardLayout from "/src/components/layouts/breadcrumbs-share-card-layout/BreadcrumbsShareCardLayout";
import {CardProps} from "/src/components/cards/card/Card";
import {ImageObject} from "/src/components/image/Image";

import {
  narrowConnection,
  constructArticleTemplatePageURL,
} from "/src/utils/utils";
import translate from "/src/utils/translate";
import {NO_CATEGORY_TYPES} from "/src/constants/common";
import buildGetStartedModuleFragmentFragment from "/src/contentful/fixture-builders/get-started-module";

import * as styles from "./help-center-article.module.scss";

enum HELP_CENTER_ARTICLE_TYPE {
  HELP_CENTER = "Help Center",
  GLOSSARY_TERM = "Glossary Term",
  MARKET_UPDATE = "Market Update",
}

const getEyebrowLabel = (type: string): string => {
  if (type === HELP_CENTER_ARTICLE_TYPE.GLOSSARY_TERM) {
    return "Glossary";
  } else if (type === HELP_CENTER_ARTICLE_TYPE.MARKET_UPDATE) {
    return "Global Logistics Update";
  } else {
    return type || "Help Center";
  }
};

const getArticleContentType = (type: string) => {
  switch (type) {
    case "Help Center":
      return "helpCenterArticle";
    case "Glossary Term":
      return "glossary";
    case "Global Logistics Update":
    case "Market Update":
      return "marketUpdate";
    default:
      return "helpCenterArticle";
  }
};

const HelpCenterArticleTemplate = ({
  data,
}: PageProps<Queries.HelpCenterArticleQueryQuery>): ReactElement => {
  const {
    contentfulHelpCenterArticle,
    allContentfulHelpCenterArticle,
    helpCenterImage,
    contentfulPromoBanner,
  } = data;

  const {
    title,
    body,
    metaTitle,
    metaDescription,
    categories,
    articleType,
    flexibleBaseContent,
    slug,
    getStartedModule,
  } = contentfulHelpCenterArticle || {};

  const moreHelpCenterArticles = narrowConnection(
    allContentfulHelpCenterArticle
  ).map(helpArticle => {
    const type = getArticleContentType(helpArticle.articleType);

    const slug =
      helpArticle.articleType === HELP_CENTER_ARTICLE_TYPE.HELP_CENTER
        ? `$${helpArticle.helpArticleId}-${helpArticle.slug}`
        : helpArticle.slug;

    return {
      eyebrow: getEyebrowLabel(helpArticle.articleType!),
      headline: helpArticle.title,
      ctas: [
        {
          label: translate("Read More"),
          url: constructArticleTemplatePageURL(slug, helpArticle.articleType),
          amplitudeCtaProps: {
            name: `more_${type}`,
            type: "link",
            linkUrl: constructArticleTemplatePageURL(
              slug,
              helpArticle.articleType
            ),
            contentType: type,
          },
        },
      ],
      image: helpArticle.flexibleBaseContent?.poster ?? helpCenterImage,
    };
  }) as Array<CardProps>;

  // let's construct legacy data and use latest flexible base content model component
  let flexibleBaseContentData: Record<string, any> = {
    slug: slug!,
    skipHeaderTags: true,
    metaDescription,
    contentModules: null,
    title,
    author: null,
    showSocialMediaIcons: false,
    publishDate: null,
    socialMediaImage: null,
    articleType: articleType,
    categoryTag:
      categories && !NO_CATEGORY_TYPES.includes(articleType!)
        ? categories[0]
        : null,
    rightSectionWidgets: [],
  };

  if (flexibleBaseContent) {
    flexibleBaseContentData = {
      ...flexibleBaseContentData,
      ...flexibleBaseContent,
    };
  } else {
    if (body?.body) {
      // legacy blog posts only have a `body`
      flexibleBaseContentData.contentModules = [
        {
          __typename: "ContentfulContentPageBodyCopy",
          body: body,
        },
      ];

      // add get started module
      const legacyGetStartedModule = buildGetStartedModuleFragmentFragment({
        layout: "plain",
        title: translate("Ready to Get Started?"),
        body: {
          body: translate(
            "Flexport makes shipping your cargo transparent, reliable, and affordable"
          ),
        },
        darkMode: false,
        ctaButtons: [
          {
            __typename: "ContentfulCtaButton",
            label: translate("Get Started"),
            url: "/sign-up",
            amplitudeCtaType: "sign_up",
            amplitudeCtaName: "blog-get-started-cta",
            openInNewTab: false,
          },
        ],
      }) as GetStartedModuleProps;

      flexibleBaseContentData.rightSectionWidgets.push(legacyGetStartedModule);
    }
  }

  return (
    <Layout promoBannerData={contentfulPromoBanner}>
      <HeaderInfo
        description={metaDescription?.metaDescription!}
        image={
          (flexibleBaseContentData.poster ??
            flexibleBaseContentData.socialMediaImage ??
            helpCenterImage) as ImageObject
        }
        title={`Flexport ${articleType || "Help Center"} | ${metaTitle}`}
      />

      <BlogHero
        type={articleType!}
        title={title!}
        categories={categories}
        showBackBtn
        showSocialIcons
        copy={metaDescription?.metaDescription!}
        eyebrow={getEyebrowLabel(articleType!)}
        bgImage={flexibleBaseContentData.poster ?? null}
        slug={slug!}
      />

      {flexibleBaseContentData && (
        <FlexibleBaseContentModel
          {...(flexibleBaseContentData as FlexibleBaseContentModelProps)}
          articleType={articleType!}
          slug={slug!}
          skipHeaderTags
        />
      )}

      <WidgetShare
        showTitle
        classes={styles.helpCenterArticleTemplate__share}
      />

      <BreadcrumbsShareCardLayout
        cards={moreHelpCenterArticles}
        cardImageClasses={styles.helpCenterArticleTemplate__cardImage}
        headline={translate("More from Flexport")}
      />

      {getStartedModule && (
        <GetStartedModule {...(getStartedModule as GetStartedModuleProps)} />
      )}
    </Layout>
  );
};

export default HelpCenterArticleTemplate;

export const query = graphql`
  query HelpCenterArticleQuery(
    $helpArticleId: Int!
    $locale: String!
    $categorySlug: String
    $localeBasedPageFilter: [String]
  ) {
    contentfulPromoBanner(
      node_locale: {eq: $locale}
      contentful_id: {eq: "17z8sGPISXmsBtu2jynvvp"}
    ) {
      ...PromoBannerFragment
    }
    contentfulHelpCenterArticle(
      helpArticleId: {eq: $helpArticleId}
      node_locale: {eq: $locale}
    ) {
      helpArticleId
      title
      slug
      summary {
        summary
      }
      body {
        body
      }
      flexibleBaseContent {
        ...FlexibleBaseContentModelFragment
      }
      tags
      metaTitle
      metaDescription {
        metaDescription
      }
      visibility
      categories {
        title
        slug
      }
      articleType
      getStartedModule {
        ...GetStartedModuleFragment
      }
    }
    allContentfulHelpCenterArticle(
      filter: {
        helpArticleId: {ne: $helpArticleId}
        categories: {elemMatch: {slug: {eq: $categorySlug}}}
        visibility: {eq: "Public"}
        node_locale: {eq: $locale}
        onlyPublishTo: {in: $localeBasedPageFilter}
      }
      sort: {datePublished: DESC}
      limit: 3
    ) {
      edges {
        node {
          helpArticleId
          slug
          title
          metaDescription {
            metaDescription
          }
          articleType
          flexibleBaseContent {
            ...FlexibleBaseContentModelFragment
          }
        }
      }
    }
    helpCenterImage: contentfulAsset(
      contentful_id: {eq: "5q64zrjMK0L6znveKEmwxa"}
    ) {
      ... on ContentfulAsset {
        ...AssetFragment
      }
      gatsbyImageData
    }
  }
`;
